/**
 * `useStreamStore` — the hot path.
 *
 * docs/06-frontend.md#zustand-client-only-state: "Per-`turnId`: accumulated text,
 * `lastSeq`, tool-call chips, status."
 *
 * **Why streaming tokens must not go through the Query cache** (same document):
 *
 * > a delta arrives every few tens of milliseconds; writing each into
 * > `queryClient.setQueryData` invalidates observers and re-renders every consumer of
 * > that key. The stream buffer lives in Zustand with a selector subscribed only by the
 * > message bubble component. On `turn_complete`, the buffer is flushed once into the
 * > Query cache and cleared. One handoff, one re-render of the transcript.
 *
 * The reducer's one non-obvious rule: **deltas with `seq <= lastSeq` are dropped**. Replay
 * after a reconnect deliberately overlaps, so exactly-once rendering is guaranteed by
 * sequence number rather than by the server and client agreeing on a boundary. Ordering
 * is by the same number, so an out-of-order arrival cannot interleave text.
 *
 * **A turn's content is one or more segments, one per author, not one flat buffer.** An
 * ordinary chat turn has exactly one — the root agent never changes mid-turn — but a
 * `research_workflow`/`build_roadmap_workflow` turn is several named agents
 * (`research_planner`, `topic_researcher`, …) writing into the *same* turn
 * (docs/03-agent-design.md#the-research-pipeline-since-m9), and the persisted transcript
 * already renders one bubble per stored *event* (`lib/transcript.ts`'s `toMessages`),
 * each with its own author. Before this, the live buffer had no such boundary: every
 * delta for a turn landed in one `text` string regardless of which node produced it, so
 * `research_planner` finishing and `topic_researcher` starting looked like one bubble
 * quietly growing rather than two messages — invisible in a single-author chat turn,
 * where it never had anything to hide, and wrong the instant a turn had more than one.
 * A new segment starts whenever a frame's `author` differs from the current last
 * segment's, mirroring the granularity the persisted view already has.
 */

import { create } from 'zustand';

export type StreamStatus = 'idle' | 'running' | 'complete' | 'error';

export interface ToolChip {
  seq: number;
  name: string;
  /** `false` until the matching `tool_result` arrives; drives the spinner on the chip. */
  done: boolean;
  ok: boolean;
  /**
   * The call's arguments, from the `tool_call` frame's `argsPreview`.
   *
   * Kept so a live chip can say the same thing a stored one does. The *result* is not
   * here — `tool_result` carries only `ok` — so a live chip's detail is written from
   * arguments alone, and the fuller version appears when the turn settles and the
   * transcript takes over. That asymmetry is why `describeTool` reads arguments first.
   */
  args: Record<string, unknown>;
}

export interface StreamSegment {
  /** ADK's `Event.author` — the root agent for ordinary chat, one of several node names
   * across a research/roadmap turn. */
  author: string;
  text: string;
  tools: ToolChip[];
}

export interface StreamState {
  turnId: string;
  sessionId: string | null;
  /** In arrival order. The last entry is the one still receiving deltas/chips. */
  segments: StreamSegment[];
  lastSeq: number;
  status: StreamStatus;
  error: { code: string; message: string; retryable: boolean } | null;
  /**
   * Monotonic, assigned when the buffer is created. It exists so "the turn this session is
   * showing" can be answered with *newest*, rather than with whichever entry a `find`
   * happened to reach first.
   *
   * A counter rather than `Date.now()`: two turns can open inside one millisecond, and a
   * tie here is exactly the bug this field was added to remove.
   */
  openedAt: number;
}

let opened = 0;

function blank(turnId: string, sessionId: string | null = null): StreamState {
  opened += 1;
  return {
    turnId,
    sessionId,
    segments: [],
    lastSeq: 0,
    status: 'running',
    error: null,
    openedAt: opened,
  };
}

/**
 * The segment a new frame from `author` belongs to: the last one, if it is already that
 * author's, or a fresh one appended after it. Never merges into anything earlier than the
 * last segment — a turn's segments are strictly chronological, the same as the persisted
 * transcript's events are.
 */
function withSegment(
  segments: StreamSegment[],
  author: string,
  update: (segment: StreamSegment) => StreamSegment,
): StreamSegment[] {
  const last = segments[segments.length - 1];
  if (last && last.author === author) {
    return [...segments.slice(0, -1), update(last)];
  }
  return [...segments, update({ author, text: '', tools: [] })];
}

/**
 * The turn a session's UI should be showing: the most recently opened one, or `null`.
 *
 * **Not `Object.values(turns).find(…)`.** That returns the *first-inserted* match, and a
 * turn only leaves the store when its text is handed to the transcript — which happens on
 * `turn_complete` and nowhere else. So a turn that ended in `turn_error` stayed forever,
 * and every later turn in that session was rendered *behind* it: the red error remained on
 * screen, the new reply streamed into a buffer nothing read, and only a reload — which
 * empties the store and refetches the transcript — showed the answer that had in fact been
 * generated. A 429 from Vertex was how this was found.
 *
 * Returning a stored object rather than a computed one matters: a Zustand selector is
 * compared with `Object.is`, so a fresh literal here would re-render forever
 * (CLAUDE.md, and `DEFAULT_FILTERS` in `stores/boardUi.ts`).
 */
export function newestTurnFor(
  turns: Record<string, StreamState>,
  sessionId: string,
): StreamState | null {
  let newest: StreamState | null = null;
  for (const turn of Object.values(turns)) {
    if (turn.sessionId !== sessionId) continue;
    if (newest === null || turn.openedAt > newest.openedAt) newest = turn;
  }
  return newest;
}

interface StreamStore {
  turns: Record<string, StreamState>;

  /** Register a turn started by `POST /turns`, before any frame has arrived. */
  begin: (turnId: string, sessionId: string) => void;
  appendDelta: (turnId: string, seq: number, text: string, author: string) => void;
  noteToolCall: (
    turnId: string,
    seq: number,
    name: string,
    author: string,
    args?: Record<string, unknown>,
  ) => void;
  noteToolResult: (turnId: string, seq: number, name: string, ok: boolean) => void;
  complete: (turnId: string, seq: number) => void;
  fail: (
    turnId: string,
    seq: number,
    error: { code: string; message: string; retryable: boolean },
  ) => void;
  /** Drop a turn's buffer after its text has been handed to the Query cache. */
  clear: (turnId: string) => void;
  /** Every turn still running, with its cursor — the reconnect's resume worklist. */
  running: () => { turnId: string; lastSeq: number }[];
}

export const useStreamStore = create<StreamStore>((set, get) => ({
  turns: {},

  begin(turnId, sessionId) {
    set((state) => {
      if (state.turns[turnId]) return state;
      // Starting a turn retires the *failed* one before it. Nothing else ever removes a
      // failed turn — `clear` is called from the `turn_complete` handoff and only there —
      // so without this the store accumulates one entry per error, for the life of the tab.
      //
      // Only `error`. A `complete` turn is dropped by that handoff, *after* its text has
      // been refetched into the transcript; dropping it here instead would race the
      // refetch and blink the coach's last message off the screen.
      const kept = Object.entries(state.turns).filter(
        ([, turn]) => !(turn.sessionId === sessionId && turn.status === 'error'),
      );
      return {
        turns: {
          ...Object.fromEntries(kept),
          [turnId]: blank(turnId, sessionId),
        },
      };
    });
  },

  appendDelta(turnId, seq, text, author) {
    set((state) => {
      const current = state.turns[turnId] ?? blank(turnId);
      // The dedupe. Replay after a reconnect overlaps on purpose.
      if (seq <= current.lastSeq) return state;
      return {
        turns: {
          ...state.turns,
          [turnId]: {
            ...current,
            segments: withSegment(current.segments, author, (segment) => ({
              ...segment,
              text: segment.text + text,
            })),
            lastSeq: seq,
            status: 'running',
          },
        },
      };
    });
  },

  noteToolCall(turnId, seq, name, author, args) {
    set((state) => {
      const current = state.turns[turnId] ?? blank(turnId);
      if (seq <= current.lastSeq) return state;
      return {
        turns: {
          ...state.turns,
          [turnId]: {
            ...current,
            lastSeq: seq,
            segments: withSegment(current.segments, author, (segment) => ({
              ...segment,
              tools: [...segment.tools, { seq, name, done: false, ok: true, args: args ?? {} }],
            })),
          },
        },
      };
    });
  },

  noteToolResult(turnId, seq, name, ok) {
    set((state) => {
      const current = state.turns[turnId] ?? blank(turnId);
      if (seq <= current.lastSeq) return state;
      // Close the most recent open chip with this name, searching segments newest first
      // — matching by name rather than by an id because the contract's `tool_result`
      // carries no call id, and the model cannot have two calls to the same tool
      // outstanding at once regardless of which segment issued the call.
      let closed = false;
      const segments = [...current.segments]
        .reverse()
        .map((segment) => {
          if (closed) return segment;
          const tools = [...segment.tools]
            .reverse()
            .map((chip) => {
              if (closed || chip.done || chip.name !== name) return chip;
              closed = true;
              return { ...chip, done: true, ok };
            })
            .reverse();
          return { ...segment, tools };
        })
        .reverse();
      return {
        turns: {
          ...state.turns,
          [turnId]: { ...current, lastSeq: seq, segments },
        },
      };
    });
  },

  complete(turnId, seq) {
    set((state) => {
      const current = state.turns[turnId] ?? blank(turnId);
      return {
        turns: {
          ...state.turns,
          [turnId]: {
            ...current,
            lastSeq: Math.max(current.lastSeq, seq),
            status: 'complete',
            segments: current.segments.map((segment) => ({
              ...segment,
              tools: segment.tools.map((chip) => ({ ...chip, done: true })),
            })),
          },
        },
      };
    });
  },

  fail(turnId, seq, error) {
    set((state) => {
      const current = state.turns[turnId] ?? blank(turnId);
      return {
        turns: {
          ...state.turns,
          [turnId]: {
            ...current,
            lastSeq: Math.max(current.lastSeq, seq),
            status: 'error',
            error,
          },
        },
      };
    });
  },

  clear(turnId) {
    set((state) => {
      const { [turnId]: _dropped, ...rest } = state.turns;
      return { turns: rest };
    });
  },

  running() {
    return Object.values(get().turns)
      .filter((turn) => turn.status === 'running')
      .map((turn) => ({ turnId: turn.turnId, lastSeq: turn.lastSeq }));
  },
}));
